import { sampleGivenName, sampleSalutation, sampleSurname } from '../../../../lib/sampleNames';

/**
 * Test data for the References form's Autogenerate.
 *
 * **The name is REAL-LOOKING, by product call on 31 Oct 2026** — `lib/sampleNames.ts` holds the
 * vocabulary and what the reversal gave up. It used to be deliberately not a name, so that a row
 * in `rsys_pii` could be told from a real person's by reading it; that protection is gone across
 * every personnel generator at once, because half of them reading as tokens and half as names
 * would be the worse of the two worlds.
 *
 * It now matches the hospital's own `sampleReference`, which has always picked from pools of
 * real Filipino given names and surnames — the capture that was deliberately diverged from here
 * is no longer a divergence.
 *
 * **The occupation, company and address are plausible**, and that is not an inconsistency: none
 * of them leaves `rsys_`, and an unreadable one would make the generated profile worse at the one
 * job it has.
 *
 * **The contact number is from the reserved fiction block** — `+63 917 555 …`, which can never
 * reach a real handset.
 */
const OCCUPATIONS = [
  'Head Nurse',
  'Department Head',
  'Barangay Captain',
  'School Principal',
  'Senior Accountant',
  'Project Supervisor',
];

const COMPANIES = [
  'Regional Health Office',
  'Philippine Nurses Association',
  'Bagong Nayon Elementary School',
  'Municipal Government',
  'Community Health Partners',
];

const ADDRESSES = [
  'Rizal, Antipolo, Bagong Nayon',
  'Metro Manila, Quezon City, Diliman',
  'Laguna, Calamba, Real',
  'Cavite, Imus, Poblacion',
];

export interface SampleReference {
  salutation: string;
  firstName: string;
  middleName: string;
  lastName: string;
  suffix: string;
  occupation: string;
  company: string;
  address: string;
  contactNumber: string;
}

function pick<T>(pool: readonly T[]): T {
  return pool[Math.floor(Math.random() * pool.length)]!;
}

export function sampleReference(): SampleReference {
  /*
    One sex decides the given name AND the title: a reference carries no sex field, so the
    salutation is the only thing stating one, and drawn apart the two contradict each other.
  */
  const sexKind: 'female' | 'male' = Math.random() < 0.5 ? 'female' : 'male';

  return {
    salutation: sampleSalutation(sexKind),
    firstName: sampleGivenName(sexKind),

    // A middle name here is the mother's MAIDEN SURNAME, so it comes off the surname list.
    middleName: sampleSurname(),
    lastName: sampleSurname(),

    // Left empty rather than invented: most people have no suffix, and generating one on every
    // click would make the default look wrong.
    suffix: '',

    occupation: pick(OCCUPATIONS),
    company: pick(COMPANIES),
    address: pick(ADDRESSES),

    // The reserved fiction block, so a generated number can never reach a real handset.
    contactNumber: `+63 917 555 ${String(1000 + Math.floor(Math.random() * 9000))}`,
  };
}
